// Vendor
import { ElementRef, EventEmitter, Renderer2, SimpleChanges, } from '@angular/core';
import Highcharts from 'highcharts';
import YchartGeneral from 'ChartApp/ts/features/general-chart/ychart-general';
// services
import { AjaxService } from 'NewAngular/services/core/ajax.service';
// Constants
import { GENERAL_CHART_DEFAULT_CONFIG_OPTIONS, HIGHCHARTS_GENERAL_DEFAULT_OPTIONS, } from 'ChartApp/ts/features/general-chart/constants';
import * as i0 from "@angular/core";
import * as i1 from "NewAngular/services/core/ajax.service";
/**
 *
 * GeneralChartComponent.
 *
 * Required attributes:
 *
 * chartDataHandler : function on the containing scope of the form funcName(data) that is called when Ajax data is
 *                     received (used to extract date range, for example).
 * chartJsonData : variable on the containing scope that contains chart data.
 * chartOptions : object on the containing parent that will override default options set for this Highchart.
 * configurableOptions: options to configure charts outside of Highcharts options, such as the tooltip bar.
 *
 * Optional attributes:
 * chartDataUrl : url to get the chart data from instead of chartDataJson.
 * tooltipDataHandler: JS Function to update tooltipData on parent for tooltip bar.
 */
export class GeneralChartComponent {
    constructor(ajaxService, eltRef, renderer) {
        this.ajaxService = ajaxService;
        this.eltRef = eltRef;
        this.renderer = renderer;
        this.chartDataHandler = new EventEmitter();
        this.tooltipDataHandler = new EventEmitter();
        this.chartElement = null;
        this.resizeEventBound = false;
        this.tooltipData = [];
        this.NO_DATA_CLASS = 'get-started-small';
        this.REBIND_CHART_RESIZE_EVENT = 'rebindChartResize';
        this.NO_DATA_MESSAGE = '<p>There are no valid items on this chart. Change or add items from the left sidebar.</p>';
        this.resizeHandler = () => {
            // Only resize if the width changes OR we are in dynamicHeight mode on resize
            if (this.windowWidth !== window.innerWidth || this.configurableOptions.dynamicHeight) {
                this.redrawChart();
            }
        };
        this.hostElement = this.eltRef.nativeElement;
        this.chartJsonData = JSON.parse(eltRef.nativeElement.getAttribute('chartJsonData'));
        this.configurableOptions = JSON.parse(eltRef.nativeElement.getAttribute('configurableOptions'));
        this.chartOptions = JSON.parse(eltRef.nativeElement.getAttribute('chartOptions'));
        this.setToolTipInChartOptions();
    }
    ngOnInit() {
        this.initAndChangeHandler(this.chartJsonData, this.chartDataUrl);
    }
    ngOnChanges(changes) {
        const chartJsonData = changes.chartJsonData?.currentValue != null ? changes.chartJsonData.currentValue : null;
        const chartDataUrl = changes.chartDataUrl?.currentValue != null ? changes.chartDataUrl.currentValue : null;
        this.initAndChangeHandler(chartJsonData, chartDataUrl);
        if (changes.chartOptions?.currentValue !== null) {
            this.setToolTipInChartOptions();
        }
    }
    ngOnDestroy() {
        this.unbindResizeHandler();
    }
    getChartData(chartDataUrl) {
        this.resetChart();
        return this.ajaxService.getDataObserveBody(chartDataUrl);
    }
    resetChart() {
        if (this.componentChart) {
            const chartIdx = Highcharts.charts.indexOf(this.componentChart);
            if (chartIdx !== -1) {
                this.componentChart.destroy();
                Highcharts.charts.splice(chartIdx, 1);
            }
        }
        if (this.tooltipDataHandler) {
            this.tooltipData = [];
            this.tooltipDataHandler.emit(this.tooltipData);
        }
    }
    displayDataError(response) {
        // // If we get an error due to a bad request, write the error message
        // If we get an error due to a bad request, write the error message
        if (response.data && response.data.err_message && response.status === 400) {
            this.NO_DATA_MESSAGE = `<p> ${response.data.err_message} </p>`;
        }
        else {
            // Otherwise, display a default error message
            this.NO_DATA_MESSAGE = '<p>There are no valid items on this chart.</p>';
        }
        this.displayNoData();
    }
    displayNoData() {
        // Remove chartData so that if the chart wasn't cleaned up nicely,
        // it doesn't reappear
        this.renderer.addClass(this.hostElement, this.NO_DATA_CLASS);
        this.hostElement.innerHTML = this.NO_DATA_MESSAGE;
        this.unbindResizeHandler();
    }
    clearChartContainer() {
        this.renderer.removeClass(this.hostElement, this.NO_DATA_CLASS);
        this.hostElement.innerHTML = '';
    }
    redrawChart() {
        setTimeout(() => {
            this.renderChart(this.chartInfo);
            this.windowWidth = window.innerWidth;
        });
    }
    renderChart(chart) {
        if (!chart)
            return;
        this.chartInfo = chart;
        this.highchartOptions = Highcharts.merge(HIGHCHARTS_GENERAL_DEFAULT_OPTIONS, this.chartOptions);
        this.configurableOptions = { ...GENERAL_CHART_DEFAULT_CONFIG_OPTIONS, ...this.configurableOptions };
        if (!this.highchartOptions.chart.height) {
            const chartDimensions = this.getChartDimensions();
            this.highchartOptions.chart.height = chartDimensions.height;
        }
        if (this.chartDataHandler)
            this.chartDataHandler.emit(this.chartInfo);
        this.resetChart();
        this.clearChartContainer();
        this.setTooltipData();
        const chartDiv = document.createElement('div');
        this.renderer.appendChild(this.hostElement, chartDiv);
        const ychartGeneral = new YchartGeneral(this.highchartOptions, this.configurableOptions);
        const processedHighchartOptions = ychartGeneral.chartGeneral(this.chartInfo);
        this.componentChart = new Highcharts.Chart(chartDiv, processedHighchartOptions);
        this.bindResizeHandler();
    }
    getChartDimensions() {
        // @ts-ignore
        const chartWidth = this.chartElement.clientWidth;
        // @ts-ignore
        const chartHeight = this.chartElement.clientHeight;
        const chartMaxHeight = Math.round(0.7 * chartWidth);
        return { height: chartHeight, width: chartWidth, maxHeight: chartMaxHeight };
    }
    bindResizeHandler(isRebinding = false) {
        // Calling `.unbind` will cause all of the charts to unbind from the "resize" event.
        // In that case, we have to rebind all of the other charts on the page, which is accomplished by setting
        // `isRebinding=true`.
        if (isRebinding || !this.resizeEventBound) {
            window.addEventListener('resize', this.resizeHandler);
            this.resizeEventBound = true;
        }
    }
    unbindResizeHandler() {
        if (this.resizeEventBound) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeEventBound = false;
            // calling unbind above will cause all charts in the window to unbind from their resize handlers
            // we must instruct them to re-bind
            // eslint-disable-next-line no-undef
            window.dispatchEvent(new Event(this.REBIND_CHART_RESIZE_EVENT));
        }
    }
    setTooltipData() {
        // We use this when we want a callback to a custom tooltip directive
        // Sets up a tooltipData array to be populated by Highchart on mouseover
        this.configurableOptions.tooltipCallback = (tooltipData) => {
            setTimeout(() => {
                this.tooltipData = tooltipData;
                this.tooltipDataHandler.emit(this.tooltipData);
            });
        };
    }
    setToolTipInChartOptions() {
        /**
         * Determine the position for the tooltip based on the pointer, the tooltip size, and the chart's distance from
         * the left.
         *
         * @param point
         * @param chart
         * @returns Array of x and y coordinates for the tooltip
         */
        function positioner(point, chart) {
            const tooltip = chart.tooltip;
            // The tooltip's actual contents are in the first descendant div inside tooltip.label.div
            const tooltipContents = tooltip.label.div.querySelector('div');
            // fallback defaults in case the tooltip measurements can't be determined
            let x = 60;
            let y = 0;
            try {
                if (tooltipContents) {
                    const computedStyle = window.getComputedStyle(tooltipContents);
                    const paddingTop = computedStyle.getPropertyValue('padding-top');
                    const paddingRight = computedStyle.getPropertyValue('padding-right');
                    const paddingBottom = computedStyle.getPropertyValue('padding-bottom');
                    const paddingLeft = computedStyle.getPropertyValue('padding-left');
                    const width = tooltipContents.clientWidth + parseFloat(paddingLeft) + parseFloat(paddingRight);
                    const height = tooltipContents.clientHeight + parseFloat(paddingTop) + parseFloat(paddingBottom);
                    const arrowHeight = 15;
                    x = chart.plotLeft + width / 2;
                    y = height / 2 - arrowHeight;
                }
            }
            catch (e) {
                // let the defaults stand
            }
            return {
                x: point.plotX + x,
                y: point.plotY + y,
            };
        }
        if (this.chartOptions) {
            this.chartOptions.tooltip = {
                ...this.chartOptions.tooltip,
                positioner: (labelWidth, labelHeight, point) => positioner(point, this.componentChart),
            };
        }
        else {
            this.chartOptions = {
                tooltip: {
                    positioner: (labelWidth, labelHeight, point) => positioner(point, this.componentChart),
                },
            };
        }
    }
    /**
     * Helper function, necessary because ngOnChanges is not triggered when bootstrap GeneralChartComponent
     */
    initAndChangeHandler(chartJsonData, chartDataUrl) {
        if (this.renderElement == null) {
            this.renderElement = this.hostElement.parentElement;
            this.chartElement = this.renderElement;
        }
        Highcharts.setOptions({ lang: { thousandsSep: '' } });
        if (chartJsonData != null) {
            this.renderChart(this.chartJsonData);
        }
        else if (chartDataUrl != null) {
            if (chartDataUrl !== '') {
                this.getChartData(chartDataUrl).subscribe({
                    next: (chartInfo) => {
                        this.renderChart(chartInfo);
                    },
                    error: (error) => {
                        this.displayDataError(error);
                        if (this.chartDataHandler)
                            this.chartDataHandler.emit({});
                    },
                });
            }
            else {
                this.displayNoData();
                this.resetChart();
            }
        }
    }
}
GeneralChartComponent.ɵfac = function GeneralChartComponent_Factory(t) { return new (t || GeneralChartComponent)(i0.ɵɵdirectiveInject(i1.AjaxService), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2)); };
GeneralChartComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: GeneralChartComponent, selectors: [["ycn-general-chart"]], inputs: { chartDataUrl: "chartDataUrl", chartJsonData: "chartJsonData", chartOptions: "chartOptions", configurableOptions: "configurableOptions" }, outputs: { chartDataHandler: "chartDataHandler", tooltipDataHandler: "tooltipDataHandler" }, features: [i0.ɵɵNgOnChangesFeature], decls: 0, vars: 0, template: function GeneralChartComponent_Template(rf, ctx) { }, encapsulation: 2 });
